import React, { useEffect } from "react";
import "@/App.css";
import "./styles/RoyalSecurity.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { BusinessStructuredData } from "./components/StructuredData";
import { Home } from "./pages/Home";
import { Services } from "./pages/Services";
import { LettingAgents } from "./pages/LettingAgents";
import { PropertyManagement } from "./pages/PropertyManagement";
import { Process } from "./pages/Process";
import { Testimonials } from "./pages/Testimonials";
import { About } from "./pages/About";
import { Contact } from "./pages/Contact";
import { Blog, BlogPost } from "./pages/Blog";
import { Admin } from "./pages/Admin";
import { Toaster } from "./components/ui/sonner";
import { VisitorTracker } from "./components/VisitorTracker";
import { FloatingButtons } from "./components/FloatingButtons";
import { applySeo, applyDefaultSeo, getPageMeta } from "./lib/seo";

// Location pages
import {
  LocksmithRomford,
  LocksmithHavering,
  LocksmithHornchurch,
  LocksmithUpminster,
  LocksmithDagenham,
  LocksmithBarking,
  LocksmithIlford,
  LocksmithChelmsford,
  LocksmithStratford,
  LocksmithCanaryWharf
} from "./pages/locations";

// Scroll to top component
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, [pathname]);

  return null;
}

// Sets title/description/canonical for every route from the shared metadata file
function RouteSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    if (path.startsWith('/blog/')) return;
    const meta = getPageMeta(path);
    if (meta) applySeo(meta);
    else applyDefaultSeo(path);
  }, [pathname]);

  return null;
}

// Layout wrapper that conditionally shows header/footer
function AppLayout({ children, showLayout }) {
  if (!showLayout) {
    return <>{children}</>;
  }
  return (
    <div className="App" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <main style={{ flex: 1 }}>{children}</main>
      <Footer />
      <Toaster />
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const isAdminPage = location.pathname === '/admin';
  
  return (
    <>
      <ScrollToTop />
      <RouteSeo />
      {!isAdminPage && <VisitorTracker />}
      {!isAdminPage && <BusinessStructuredData />}
      {!isAdminPage && <FloatingButtons />}
      <AppLayout showLayout={!isAdminPage}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/letting-agents" element={<LettingAgents />} />
          <Route path="/property-management" element={<PropertyManagement />} />
          <Route path="/process" element={<Process />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/admin" element={<Admin />} />
          
          {/* Local Service Area Pages */}
          <Route path="/locksmith-romford" element={<LocksmithRomford />} />
          <Route path="/locksmith-havering" element={<LocksmithHavering />} />
          <Route path="/locksmith-hornchurch" element={<LocksmithHornchurch />} />
          <Route path="/locksmith-upminster" element={<LocksmithUpminster />} />
          <Route path="/locksmith-dagenham" element={<LocksmithDagenham />} />
          <Route path="/locksmith-barking" element={<LocksmithBarking />} />
          <Route path="/locksmith-ilford" element={<LocksmithIlford />} />
          <Route path="/locksmith-chelmsford" element={<LocksmithChelmsford />} />
          <Route path="/locksmith-stratford" element={<LocksmithStratford />} />
          <Route path="/locksmith-canary-wharf" element={<LocksmithCanaryWharf />} />
        </Routes>
      </AppLayout>
      {!isAdminPage && <Toaster />}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
